/**
 * Compact “add tag” control for Corporate hero pills.
 * Frosted horizontal type strip — same placement/glass as person pills.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import {
  CORP_PILL_TYPE_CYCLE,
  CORP_PILL_TYPE_META,
} from '../../utils/corporateSearchPills';
import {
  HERO_PILL_MENU_OPTION,
  HERO_PILL_MENU_OPTION_IDLE,
  HERO_PILL_MENU_SHELL,
  placeHeroPillMenu,
} from '../../utils/heroPillMenuPlacement';

/**
 * @param {{
 *   compact?: boolean,
 *   onPickType?: (type: string) => void,
 *   onFocusInput?: () => void,
 * }} props
 */
export default function CorporateAddContextControl({
  compact = false,
  onPickType,
  onFocusInput,
}) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, placement: 'below' });
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const listId = useId();

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return undefined;
    const place = () => {
      const rect = btnRef.current.getBoundingClientRect();
      const menuEl = menuRef.current;
      setMenuPos(
        placeHeroPillMenu(rect, {
          width: menuEl?.offsetWidth || 228,
          height: menuEl?.offsetHeight || 44,
          gap: 8,
        }),
      );
    };
    place();
    const raf = requestAnimationFrame(place);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (
        btnRef.current?.contains(e.target) ||
        menuRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleOpen = () => {
    setOpen((v) => !v);
    onFocusInput?.();
  };

  return (
    <div className="relative shrink-0" data-testid="corporate-add-context">
      <button
        ref={btnRef}
        type="button"
        aria-label="Add corporate tag"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listId : undefined}
        onClick={handleOpen}
        className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-700 bg-slate-800 text-xs font-bold text-slate-300 transition hover:border-cyan-500/40 hover:text-cyan-400"
        data-testid={compact ? 'corporate-add-context-compact' : 'corporate-add-context-full'}
      >
        <span aria-hidden>+</span>
      </button>

      {open ? (
        <div
          ref={menuRef}
          id={listId}
          role="listbox"
          aria-label="Corporate tag type"
          data-placement={menuPos.placement}
          data-testid="corporate-add-context-menu"
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 90 }}
          className={HERO_PILL_MENU_SHELL}
        >
          {CORP_PILL_TYPE_CYCLE.map((opt) => {
            const meta = CORP_PILL_TYPE_META[opt];
            return (
              <button
                key={opt}
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => {
                  onPickType?.(opt);
                  onFocusInput?.();
                  setOpen(false);
                }}
                className={`${HERO_PILL_MENU_OPTION} ${HERO_PILL_MENU_OPTION_IDLE}`}
              >
                <span aria-hidden className="text-sm leading-none">
                  {meta.emoji}
                </span>
                <span className="hidden min-[380px]:inline">{meta.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
